import { useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useSnack } from 'shared/ui/snack';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { ErrorBanner } from 'shared/ui/error-banner';
import { AddRegionDialog } from 'entities/geo/regions/add-region-dialog';

import { FormValues } from 'entities/geo/regions/region-form';
import useConfirmDialog from 'hooks/confirm-dialog.hook';
import { CreateRegionDto } from '@sr/dto';
import { Heading } from 'shared/ui/list-page-heading';
import { GeoListFilter } from 'features/geo/geo-list-filter/ui';
import { useSearchParams } from 'react-router-dom';
import { usePagingParams } from 'shared/hooks/paging-params';
import { useDeleteRegion, useRegionsList } from 'hooks/regions-list.hook';
import { regionListApi } from 'entities/geo/regions/hook/apiSlice';
import RegionsList from 'entities/geo/regions/regions-list';

const RegionsListPage = () => {
  const [searchParams] = useSearchParams();
  
  const getRegionSearchParams = useMemo(() => {
    const relatedFields = ['name', 'countryId'];
    const newParams = new URLSearchParams();
    [...searchParams.entries()].forEach(([key, value]) => {
      if (relatedFields.includes(key)) {
        newParams.append(key, value);
      }
    });
    return newParams;
  }, [searchParams]);

  const { isLoading: loading, error, collection: regionsList } = useRegionsList(getRegionSearchParams);

  const { showSuccess, showError } = useSnack();
  const showConfirmDialog = useConfirmDialog();

  const [isAddDialogOpen, setAddDialogOpen] = useState(false);

  const [addRegion] = regionListApi.useAddRegionMutation();

  const handleAddRegion = (data: FormValues) => {
    const createRegionDto: CreateRegionDto = {
      name: data.name,
      countryId: Number(data.countryId)
    };

    return addRegion(createRegionDto)
      .unwrap()
      .then(() => {
        setAddDialogOpen(false);
        showSuccess('Регион успешно добавлен');
      })
      .catch((e: Error) => showError(`Ошибка добавления региона: ${e.message}`));
  };

  const handleDeleteRegion = useDeleteRegion();

  if (loading)
    return <LoadingBanner />;

  if (error)
    return <ErrorBanner errorMessage={error} />;

  return (
    <>
      <Heading
        sx={{ mb: 2 }}
        title="Регионы"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Добавить
          </Button>}
      />

      <Box mb={1}>
        <GeoListFilter withCountrySearch={true}/>
      </Box>

      <RegionsList list={regionsList}  onDelete={handleDeleteRegion} />

      <AddRegionDialog
        open={isAddDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSubmit={handleAddRegion}
      />

    </>
  );
};

export default RegionsListPage;
